"use client";

import type React from "react";
import { useCallback, useRef, useState } from "react";
import { BASE64_MODULO, BASE64_REGEX } from "@/lib/auth/login-flow";
import { type IdentityBackup, validateIdentityBackup } from "@/lib/bap/identity-backup";
import { logger } from "@/lib/logger";

/** Custom hook encapsulating backup import state and handlers */
export function useBackupImport(setError: (e: string) => void, setHasLocalBackup: (v: boolean) => void, setImportedUnencryptedBackup: (b: IdentityBackup | null) => void, setShowSetPasswordForm: (v: boolean) => void) {
  const [pendingEncryptedBackup, setPending] = useState<string | null>(null);
  const pendingRef = useRef<string | null>(null);
  const setPendingEncryptedBackup = useCallback((value: string | null) => {
    pendingRef.current = value;
    setPending(value);
  }, []);
  const isPendingCandidate = useCallback((value_0: string) => pendingRef.current === value_0, []);
  const parseUnencryptedBackup = useCallback((text: string) => {
    try {
      return validateIdentityBackup(JSON.parse(text));
    } catch {
      return null;
    }
  }, []);
  const validateEncryptedBackup = useCallback((text_0: string) => {
    const trimmedText = text_0.trim();
    const cleanedText = trimmedText.replace(/\s+/g, "");
    if (cleanedText.length > 0 && BASE64_REGEX.test(cleanedText) && cleanedText.length % BASE64_MODULO === 0) {
      return cleanedText;
    }
    return null;
  }, []);
  const importBackupText = useCallback(
  // biome-ignore lint/suspicious/useAwait: moved verbatim from app/(auth)/login/page.tsx (OPL-4289). Callers await this handler, so the async signature is part of its contract; the rule was only silent before because Next's preset disables it inside page.tsx.
  async (text_1: string) => {
    setError("");
    try {
      const unencryptedBackup = parseUnencryptedBackup(text_1);
      if (unencryptedBackup) {
        setPendingEncryptedBackup(null);
        setImportedUnencryptedBackup(unencryptedBackup);
        setShowSetPasswordForm(true);
        setHasLocalBackup(false);
        return;
      }
      const cleanedEncrypted = validateEncryptedBackup(text_1);
      if (!cleanedEncrypted) {
        throw new Error("Invalid backup file format");
      }
      setPendingEncryptedBackup(cleanedEncrypted);
      setHasLocalBackup(true);
    } catch {
      logger.error({
        operation: "import_backup"
      }, "Import failed");
      setError("Invalid backup file. Please select a valid backup (encrypted or decrypted).");
    }
  }, [setPendingEncryptedBackup, parseUnencryptedBackup, validateEncryptedBackup, setError, setHasLocalBackup, setImportedUnencryptedBackup, setShowSetPasswordForm]);
  const importBackupFile = useCallback(async (file: File) => {
    const text_2 = await file.text();
    await importBackupText(text_2);
  }, [importBackupText]);
  const handleImportBackup = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file_0 = e.target.files?.[0];
    e.target.value = "";
    if (!file_0) {
      return;
    }
    await importBackupFile(file_0);
  }, [importBackupFile]);
  return {
    pendingEncryptedBackup,
    setPendingEncryptedBackup,
    isPendingCandidate,
    importBackupText,
    importBackupFile,
    handleImportBackup
  };
}